/**
 * Clean up's model reading (docs/screenshot-cleanup/PLAN.md, phase 1): one request with every image of a note, in order, and the
 * words the person typed, answered as JSON. The same prompt and parser serve the Worker route, the model comparison and the
 * tests, so what is compared is what ships. The model copies text; it never decides a code, a date or anything code can check.
 */
import { completeBooking } from './booking'

export const READ_VERSION = 'read-v15'

export type ItemKind = 'post' | 'article' | 'recipe' | 'ticket' | 'reservation' | 'appointment' | 'other'
/** A region of a screenshot, [ymin, xmin, ymax, xmax] from 0 to 1000. */
export type Box = [number, number, number, number]
export interface ReadMedia {
  image: number
  box: [number, number, number, number]
  about: string
  /** App interface drawn over the picture (buttons, counts, author row, caption, chips, comment box), from the picture check (picture.ts); code crops around them. */
  overlays?: Array<[number, number, number, number]>
  /** Words written on the picture itself (a quote card, a slide), line by line as shown; they are in the item's text too. Absent before read-v9. */
  words?: string[]
}
/** A recipe's parts, each copied as written; code converts units and adds headings, the model never rewrites them. */
export interface RecipeParts {
  servings: string | null; time: string | null; ingredients: string[]; steps: string[]; notes: string[]
  /** The post's own sentences describing the dish, wherever they appear; shown under the title when code finds them in the text. Absent before read-v9. */
  intro?: string[]
}
/** A time or deadline with its own label ("Doors 19:00", "Free cancellation until Thu 22 Oct, 19:30"), copied as shown. */
export interface LabelledText { label: string | null; text: string }
/**
 * A ticket's, reservation's or appointment's parts, each copied exactly as shown; code works out the dates (booking.ts) and the check
 * confirms them against the screenshots (verify.ts). A code's content is never here: only the device's decoder reads codes.
 */
export interface BookingParts {
  /** The event, restaurant, flight or service ("THE LANTERNS", "Olive & Ash", "LX 318"). */
  what: string | null
  /** The organiser, venue, airline or person ("Harbour Hall", "Dr. R. Patel"). */
  with: string | null
  /** The main date and time as shown ("Sat 24 Oct 2026 · Show 20:00"). */
  when: string | null
  times: LabelledText[]
  where: string | null
  seats: string[]
  people: string | null
  reference: string | null
  deadlines: LabelledText[]
  status: string | null
  bring: string[]
  /** The place's phone number as shown (read-v13), for Call. */
  phone?: string | null
  /** Fields the check could not find in the screenshots as read (verify.ts); shown to be checked, never hidden. */
  unconfirmed?: string[]
  /** Lines with a date or time that the check saw and no field holds (a deadline the reading missed); shown as they are. */
  alsoShown?: string[]
}
export interface ReadItem {
  kind: ItemKind
  platform: string | null
  author: { name: string | null; handle: string | null }
  /** As the screen showed it ("2h", "3 days ago", "Oct 18, 2026"); worked out into a date by code, not the model. */
  posted: string | null
  title: string | null
  /** What the item is for, in a reader's words ("Boarding pass", "Tips list"), read-v13; Browse uses it when code cannot tell. */
  purpose?: string | null
  /** The item's own words, verbatim, as Markdown (paragraphs, lists, headings, emphasis as displayed). */
  text: string
  media: ReadMedia[]
  tags: string[]
  link: string | null
  /** 1-based positions of the images this item came from. */
  images: number[]
  /** Where words seem to be missing (a screenshot cut off with no overlap), in the model's words; null when complete. */
  gap: string | null
  /** For a recipe: its parts, so the card can show headed sections in the person's units. */
  recipe: RecipeParts | null
  /** For a ticket, reservation or appointment: its parts, for the booking card and the suggestions. */
  booking?: BookingParts | null
}
export interface ReadResult {
  version: typeof READ_VERSION
  items: ReadItem[]
  /** Images that are plain photos, with no item text. */
  photos: number[]
  /** Images that show a picture belonging to an item (a photo, video frame, chart), app controls or not. Absent before read-v3. */
  pictures?: number[]
  /** Passages of the person's own note text that were copied from an item (for the replace-or-extend offer). */
  fromNote: string[]
  language: string
}

const KINDS: readonly ItemKind[] = ['post', 'article', 'recipe', 'ticket', 'reservation', 'appointment', 'other']
export const BOOKING_KINDS: readonly ItemKind[] = ['ticket', 'reservation', 'appointment']
const LIMITS = { items: 8, text: 20_000, field: 200, tags: 30, media: 8, fromNote: 20 }

export function buildReadPrompt(noteText: string, imageCount: number, language = 'en'): string {
  const typed = noteText.trim() ? noteText.trim().slice(0, 4000) : '(nothing)'
  // With no screenshots, the note's text is a message shared from another app (a booking confirmation): it is what is read.
  const shared = imageCount === 0
  return [
    shared
      ? 'You read a message shared into a note from another app, given as text. No images: media, photos, screens, images and fromNote are [].'
      : `You read ${imageCount} phone screenshot${imageCount === 1 ? '' : 's'} saved in one note, in order, and the words typed in it.`,
    'Return what was saved, minus app interface, as one JSON object. Copy exactly; never summarise, translate, correct, reorder or invent. Each word in its own script (ח, not ح).',
    '- Overlapping screenshots: each line once. A post\'s screenshots may come in any order: follow its own order (numbering, slide counter, running sentences).',
    '- Leave out bars, buttons, counts, others\' comments, ads, "See translation", "more", navigation, captions over moving video. Keep @mentions; closing hashtags go in "tags".',
    '- One item per post, recipe or booking; one order\'s tickets are one item; a confirmation of several flights, trains or stays (an outbound and a return flight) gives one item each.',
    '  A photo with no text is not an item: list it in "photos". Text cut off: keep what shows, say what is missing in "gap".',
    '- "text": all of the item\'s words, a booking\'s too, as Markdown ("1." or "-" lists, "#" headings, **bold**, *italic* as shown), the words on its slides and pictures included.',
    '- A slide that is only text (a numbered list, tips, a long quote on a plain background), even one that fills the screen, is text, never a picture: leave it out of "media"; its',
    '  screen has "picture": false. Only a short quote set on art or a photo stays a picture.',
    '- "media": the item\'s pictures (photo, chart, drawing, video frame; not avatars or icons), best first (a recipe\'s dish filling the frame before a hand, packaging or a person);',
    '  "image" the screenshot it is on, counted from 1 in the order given; "box" [ymin,xmin,ymax,xmax] 0–1000 on the picture\'s own edges, without text, background or app',
    '  controls; "words": every line written on it, its author too, [] for a photo.',
    '  Its main picture (a recipe\'s dish, a post\'s photo or chart) is always listed, even a video frame under app controls (they are handled separately).',
    '  "screens": one per screenshot, "picture" true when it shows an item\'s photo, chart, drawing or video frame, false for text.',
    '- "title": its heading as shown (a recipe\'s dish, a list\'s heading), or a caption\'s first line naming it; never write one. "purpose" in 2–4 words: Boarding pass, Concert ticket,',
    '  Train ticket, Restaurant reservation, Hotel booking, Dentist appointment, Recipe, Tips list (a list of points), Opinion post, News article. "fromNote": typed passages copying an item.',
    '- "recipe" (else null): "servings" with its word ("Serves 4"); "ingredients" one per line as written, no bullet ("For the sauce:" is one); "steps" one per step, without its number;',
    '  "intro" the lines describing the dish, never a call to action, question, tip or step; "notes" every other line of the recipe, in order. Add nothing.',
    '- "booking" for a ticket (event, train, flight, boarding pass), reservation (restaurant, hotel) or appointment (else null), each part as written, never completed: "what" (event, place,',
    '  flight or service), "with" (organiser, airline, person), "when" (main date and time, e.g. "Sat 24 Oct 2026 · Show 20:00"), "times" and "deadlines" (each with its label),',
    '  "where" (venue or address), "seats" (every seat, row, section, entrance, gate, terminal, zone, boarding group, platform, coach, table or seating shown, each its own string',
    '  with its label: "Seat 14C", "Gate C21", "Zone 7", "Standing", "Indoor"; not counts or class), "people", "reference" (booking code, not a boarding sequence number), "status",',
    '  "bring" (what to bring or do first), "phone";',
    '  "link": the place\'s website. Leave out the booking app\'s own features (manage, directions, menu, bill, points).',
    '- null or [] when not shown. Never guess a year, address or time; never copy a QR code or barcode.',
    '{"items":[{"kind":"post|article|recipe|ticket|reservation|appointment|other","platform":"X|Instagram|Substack|…|web","author":{"name":null,"handle":null},"posted":null,',
    '"title":null,"purpose":null,"text":"","tags":[],"link":null,"images":[1],"gap":null,"media":[{"image":1,"box":[0,0,1000,1000],"about":"","words":[]}],',
    '"recipe":{"servings":null,"time":null,"intro":[],"ingredients":[],"steps":[],"notes":[]},"booking":{"what":null,"with":null,"when":null,"times":[{"label":"Doors","text":"19:00"}],',
    '"where":null,"seats":[],"people":null,"reference":null,"deadlines":[],"status":null,"bring":[],"phone":null}}],"photos":[],"screens":[{"image":1,"picture":false}],"fromNote":[],',
    `"language":"${language}"}`,
    shared ? `The shared message: ${JSON.stringify(typed)}` : `The person typed: ${JSON.stringify(typed)}`,
  ].join('\n')
}

const record = (value: unknown): Record<string, unknown> => (value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {})
const text = (value: unknown, max = LIMITS.field): string | null => {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed && trimmed.toLowerCase() !== 'null' ? trimmed.slice(0, max) : null
}
const indices = (value: unknown, count: number): number[] =>
  Array.isArray(value) ? [...new Set(value.filter((n): n is number => Number.isInteger(n) && n >= 1 && n <= count))] : []

const lines = (value: unknown, max = 200): string[] =>
  (Array.isArray(value) ? value : []).flatMap(entry => { const line = text(entry, 2000); return line ? [line] : [] }).slice(0, max)
// A part given as {label, text} instead of one string ("Seat" and "35K") is kept as "Seat 35K", not dropped.
const labelledLines = (value: unknown, max = 200): string[] => lines((Array.isArray(value) ? value : []).map(entry => {
  if (!entry || typeof entry !== 'object') return entry
  const part = entry as Record<string, unknown>
  return [part.label, part.text ?? part.value].filter(item => typeof item === 'string' && item.trim()).join(' ')
}), max)
// A boarding sequence number ("SEQ: 0042", "Seq 42") is not a booking reference.
export const NOT_A_REFERENCE = /^(?:seq(?:uence)?|bcbp|boarding\s*(?:no|number|seq))\b/i

// A ticket count or travel class is not a seat, in any language ticketWords.ts supports; a boarding group or zone is kept.
const NOT_A_SEAT = /^(?:(?:ticket|billet|biglietto|entrada|karte|eintrittskarte)\s+\d+\s+(?:of|von|sur|de|di)\s+\d+|(?:economy|premium(?:\s+economy)?|business|first|turista|touriste|economica)(?:\s+(?:class|classe|clase|klasse))?|(?:class|classe|clase|klasse)\s+\S+|\d\.?\s+klasse)$/i
const labelled = (value: unknown): LabelledText[] => (Array.isArray(value) ? value : []).flatMap(entry => {
  const part = record(entry)
  const words = text(part.text, 300)
  return words ? [{ label: text(part.label, 80), text: words }] : []
}).slice(0, 12)

const completed = (booking: BookingParts | null, words: string): BookingParts | null => (booking ? completeBooking(booking, words, Date.now()) : null)

function bookingParts(value: unknown): BookingParts | null {
  const parts = record(value)
  const booking: BookingParts = {
    what: text(parts.what, 200), with: text(parts.with, 200), when: text(parts.when, 200), times: labelled(parts.times), where: text(parts.where, 300),
    // A ticket count, boarding group or travel class is not a seat (it is still in the item's text).
    seats: labelledLines(parts.seats, 20).map(line => line.slice(0, 200)).filter(line => !NOT_A_SEAT.test(line)), people: text(parts.people, 200), reference: NOT_A_REFERENCE.test(text(parts.reference, 80) ?? '') ? null : text(parts.reference, 80),
    deadlines: labelled(parts.deadlines), status: text(parts.status, 80), bring: lines(parts.bring, 12).map(line => line.slice(0, 300)), phone: text(parts.phone, 40),
  }
  return booking.what || booking.when || booking.reference || booking.where ? booking : null
}

function recipeParts(value: unknown): RecipeParts | null {
  const parts = record(value)
  const intro = lines(parts.intro, 6)
  const recipe = { servings: text(parts.servings, 80), time: text(parts.time, 80), ingredients: lines(parts.ingredients), steps: lines(parts.steps), notes: lines(parts.notes), ...(intro.length ? { intro } : {}) }
  return recipe.ingredients.length || recipe.steps.length ? recipe : null
}

/** A box of four numbers whose closing bracket the model left out, followed straight by the next field or the end of its object. */
const UNCLOSED_BOX = /(\[\s*-?\d+(?:\.\d+)?(?:\s*,\s*-?\d+(?:\.\d+)?){3})(\s*)(?=,\s*"|\})/g

/** Closes lists the model left open: a `}` while a list is open closes the list first, and anything open at the end is closed. */
function balanced(body: string): string {
  const open: string[] = []
  let out = '', inString = false, escaped = false
  for (const char of body) {
    if (inString) {
      out += char
      if (escaped) escaped = false
      else if (char === '\\') escaped = true
      else if (char === '"') inString = false
      continue
    }
    if (char === '"') inString = true
    else if (char === '{' || char === '[') open.push(char)
    else if (char === '}') { while (open.at(-1) === '[') { out += ']'; open.pop() } open.pop() }
    else if (char === ']') { while (open.at(-1) === '{') { out += '}'; open.pop() } open.pop() }
    out += char
  }
  return out + open.reverse().map(char => (char === '[' ? ']' : '}')).join('')
}

/**
 * The JSON object in a reply, even when a model wraps it in a code fence or adds a word around it. Flash-Lite now and then drops a
 * closing bracket (after a picture's box, or after a list of boxes), which would otherwise lose the whole reply, so those are put back.
 */
export function jsonIn(content: string): unknown {
  // A reply that is only the list of items, without its {"items": …} around it (seen with two flights in one confirmation).
  const opening = content.search(/[[{]/)
  if (content[opening] === '[') {
    try {
      const list: unknown = JSON.parse(content.slice(opening, content.lastIndexOf(']') + 1))
      // Or the whole object inside a list: [{"items": …}].
      if (Array.isArray(list)) return list.length === 1 && Array.isArray(record(list[0]).items) ? list[0] : { items: list }
    } catch { /* read as an object below */ }
  }
  const start = content.indexOf('{'), end = content.lastIndexOf('}')
  if (start < 0 || end <= start) throw new Error('The reply holds no JSON object.')
  const body = content.slice(start, end + 1)
  try { return JSON.parse(body) } catch (error) {
    const repaired = balanced(body.replace(UNCLOSED_BOX, '$1]$2'))
    if (repaired === body) throw error
    return JSON.parse(repaired)
  }
}

/** Reads and bounds a model reply; anything malformed is dropped rather than shown. Throws only when there is no JSON at all. */
export function parseRead(content: string, imageCount: number): ReadResult {
  const body = record(jsonIn(content))
  const items: ReadItem[] = (Array.isArray(body.items) ? body.items : []).slice(0, LIMITS.items).flatMap(raw => {
    const item = record(raw)
    const words = text(item.text, LIMITS.text)
    if (!words) return []
    const author = record(item.author)
    const media: ReadMedia[] = (Array.isArray(item.media) ? item.media : []).slice(0, LIMITS.media).flatMap(m => {
      const entry = record(m)
      const image = Number(entry.image)
      const box = Array.isArray(entry.box) ? entry.box.map(Number) : []
      if (!Number.isInteger(image) || image < 1 || image > imageCount || box.length !== 4 || box.some(v => !Number.isFinite(v))) return []
      const [y0, x0, y1, x1] = box.map(v => Math.max(0, Math.min(1000, Math.round(v)))) as [number, number, number, number]
      const overlays = (Array.isArray(entry.overlays) ? entry.overlays : []).slice(0, 12).flatMap(o => {
        const edges = Array.isArray(o) ? o.map(Number) : []
        if (edges.length !== 4 || edges.some(v => !Number.isFinite(v))) return []
        const [a, b, c, d] = edges.map(v => Math.max(0, Math.min(1000, Math.round(v)))) as [number, number, number, number]
        return c > a && d > b ? [[a, b, c, d] as [number, number, number, number]] : []
      })
      const words = lines(entry.words, 20).map(line => line.slice(0, 400))
      return y1 > y0 && x1 > x0 ? [{ image, box: [y0, x0, y1, x1] as [number, number, number, number], about: text(entry.about) ?? '', ...(overlays.length ? { overlays } : {}), ...(words.length ? { words } : {}) }] : []
    })
    return [{
      kind: KINDS.includes(item.kind as ItemKind) ? item.kind as ItemKind : 'other',
      platform: text(item.platform, 40),
      author: { name: text(author.name), handle: text(author.handle) },
      posted: text(item.posted, 60),
      title: text(item.title),
      purpose: text(item.purpose, 40),
      text: words,
      media,
      tags: (Array.isArray(item.tags) ? item.tags : []).flatMap(t => { const tag = text(t, 60); return tag ? [tag.startsWith('#') ? tag : `#${tag}`] : [] }).slice(0, LIMITS.tags),
      link: text(item.link, 500),
      images: indices(item.images, imageCount),
      gap: text(item.gap),
      recipe: recipeParts(item.recipe),
      // What the reading left out of a booking but its own text holds (a departure time, the route) is filled from that text.
      ...(BOOKING_KINDS.includes(item.kind as ItemKind) ? { booking: completed(bookingParts(item.booking), words) } : {}),
    }]
  })
  return {
    version: READ_VERSION,
    items,
    photos: indices(body.photos, imageCount),
    pictures: (Array.isArray(body.screens) ? body.screens : []).flatMap(screen => {
      const entry = record(screen)
      return entry.picture === true ? indices([entry.image], imageCount) : []
    }),
    fromNote: (Array.isArray(body.fromNote) ? body.fromNote : []).flatMap(p => { const passage = text(p, 2000); return passage ? [passage] : [] }).slice(0, LIMITS.fromNote),
    language: text(body.language, 10) ?? 'en',
  }
}
